/**
 * API client.
 *
 * Two things here are not incidental:
 *
 *  - A 409 from a run or purchase carries a structured `Blocked` payload, not
 *    a string. The grid renders it against the offending row rather than in a
 *    toast, so the person sees *which* item is short and by how much.
 *  - Writes queue to IndexedDB when the connection drops (A26). A supervisor
 *    who loses a half-entered shift once goes back to paper permanently.
 */

import type {
  AiStatus,
  AskResult,
  Blocked,
  DispatchBoard,
  ExecutiveBoard,
  InkBoard,
  RecyclingBoard,
  Heartbeat,
  InkDaily,
  Item,
  Location,
  LoomCell,
  LoomLineIn,
  LoomShiftResult,
  LoginResponse,
  Machine,
  Party,
  ReasonCode,
  RecipeResult,
  Run,
  RunPayload,
  StockResponse,
  Variance,
} from "./types";
import { flushQueue, queueWrite, type Rejected } from "./offline";
import type { Compare, Period } from "./period";

/**
 * `period_from` and `period_to`, as every period endpoint takes them, plus
 * the shift and the comparison where a board takes those.
 */
function periodQuery(period: Period, compare: Compare = "prior"): string {
  return `period_from=${period.from}&period_to=${period.to}` +
    (period.shift ? `&shift=${period.shift}` : "") +
    (compare !== "prior" ? `&compare=${compare}` : "");
}

// Same origin in production: the built app is served by the API itself, so
// an empty base makes every call relative. Only `npm run dev` needs an
// absolute host, and Vite's own proxy handles that.
const BASE =
  import.meta.env.VITE_API_BASE ??
  (import.meta.env.DEV ? "http://localhost:8000" : "");
const TOKEN_KEY = "npi.token";

export class ApiError extends Error {
  constructor(
    public status: number,
    message: string,
    public blocked?: Blocked
  ) {
    super(message);
    this.name = "ApiError";
  }
}

export class OfflineQueued extends Error {
  constructor(public queueId: string) {
    super("Saved on this device. It will sync when the connection returns.");
    this.name = "OfflineQueued";
  }
}

export function getToken(): string | null {
  return localStorage.getItem(TOKEN_KEY);
}

export function setToken(token: string | null): void {
  if (token) localStorage.setItem(TOKEN_KEY, token);
  else localStorage.removeItem(TOKEN_KEY);
}

function isBlocked(value: unknown): value is Blocked {
  return (
    typeof value === "object" &&
    value !== null &&
    "short_by" in value &&
    "available" in value
  );
}

/** The event every screen can listen for; AppShell shows it as a banner. */
export const STOCK_WARNING_EVENT = "neg:stock-warnings";

/**
 * Negative stock is allowed, but whoever entered it is told straight away.
 * The server puts each shortfall on a response header rather than in every
 * route's body, so this one place hears about all of them, whichever screen
 * made the request.
 */
function announceStockWarnings(response: Response): void {
  if (!response.ok) return;
  const raw = response.headers.get("X-Stock-Warnings");
  if (!raw) return;
  try {
    const detail = JSON.parse(raw) as Blocked[];
    if (detail.length) {
      window.dispatchEvent(new CustomEvent(STOCK_WARNING_EVENT, { detail }));
    }
  } catch {
    // A malformed header is not worth failing a saved entry over.
  }
}

export async function request<T>(
  path: string,
  { method = "GET", body, queueable = false }: {
    method?: string;
    body?: unknown;
    queueable?: boolean;
  } = {}
): Promise<T> {
  const headers: Record<string, string> = {};
  const token = getToken();
  if (token) headers.Authorization = `Bearer ${token}`;
  if (body !== undefined) headers["Content-Type"] = "application/json";

  let response: Response;
  try {
    response = await fetch(`${BASE}${path}`, {
      method,
      headers,
      body: body === undefined ? undefined : JSON.stringify(body),
    });
  } catch {
    // The network is gone. Buffer the write rather than losing the shift.
    if (queueable && body !== undefined) {
      const queueId = await queueWrite({ path, method, body });
      throw new OfflineQueued(queueId);
    }
    throw new ApiError(0, "Cannot reach the server. Check the connection.");
  }

  announceStockWarnings(response);

  if (response.status === 204) return undefined as T;

  const payload = await response.json().catch(() => null);

  if (!response.ok) {
    const detail = payload?.detail;
    if (isBlocked(detail)) {
      throw new ApiError(response.status, detail.message, detail);
    }
    const message =
      typeof detail === "string"
        ? detail
        : Array.isArray(detail) && detail[0]?.msg
          ? detail[0].msg
          : `Request failed (${response.status})`;
    // A 401 normally means the session is gone, so the stored token is
    // cleared. The MFA endpoints are the exception: a mistyped code must not
    // discard the challenge token, or one wrong digit throws the person back
    // to the password screen with no explanation.
    if (response.status === 401 && !path.startsWith("/api/auth/mfa/")) {
      setToken(null);
    }
    throw new ApiError(response.status, message);
  }

  return payload as T;
}

/**
 * Replay everything buffered while the connection was down.
 *
 * The queue existed and filled, but nothing ever emptied it: `flushQueue` had
 * no caller, so a shift entered offline sat in IndexedDB behind a "pending
 * sync" pill that never cleared. Queueing a write and never sending it is
 * worse than refusing it, because the supervisor believes it is saved.
 *
 * Replayed in the order written — a run that consumes material must not
 * overtake the receipt that supplied it, or the negative-stock block will
 * correctly refuse it.
 */
export interface SyncResult {
  sent: number;
  failed: number;
  kept: number;
  rejected: Rejected[];
}

// One replay at a time: two in flight would send the same entry twice. A
// second caller joins the run already going rather than being told "nothing
// happened" — otherwise whichever caller arrives second reports zero and the
// person is never told what synced. That is not hypothetical: the arrival
// drain and an `online` event routinely fire together, and React's StrictMode
// double-invokes the effect in development.
let inFlight: Promise<SyncResult> | null = null;

export function syncQueue(): Promise<SyncResult> {
  if (inFlight) return inFlight;

  inFlight = flushQueue((entry) => {
    const headers: Record<string, string> = {
      "Content-Type": "application/json",
    };
    const token = getToken();
    if (token) headers.Authorization = `Bearer ${token}`;
    return fetch(`${BASE}${entry.path}`, {
      method: entry.method,
      headers,
      body: JSON.stringify(entry.body),
    });
  }).finally(() => {
    inFlight = null;
  });

  return inFlight;
}

export const api = {
  // -- Auth ---------------------------------------------------------------
  async login(email: string, password: string): Promise<LoginResponse> {
    const session = await request<LoginResponse>("/api/auth/login", {
      method: "POST",
      body: { email, password },
    });
    setToken(session.token);
    return session;
  },

  logout(): void {
    setToken(null);
  },

  me: () => request<LoginResponse["user"]>("/api/auth/me"),

  /** Rebuild a session from a stored token: the user *and* what they may open. */
  session: () =>
    request<{ user: LoginResponse["user"]; modules: string[] }>(
      "/api/auth/session"
    ),

  parties: (role?: "customer" | "supplier" | "contractor") =>
    request<Party[]>(`/api/masters/parties${role ? `?role=${role}` : ""}`),

  locations: () => request<Location[]>("/api/masters/locations"),

  // -- Public -------------------------------------------------------------
  heartbeat: () => request<Heartbeat>("/api/public/heartbeat"),

  // -- Masters ------------------------------------------------------------
  items: (itemType?: string) =>
    request<Item[]>(
      `/api/masters/items${itemType ? `?item_type=${itemType}` : ""}`
    ),
  machines: (stage?: string) =>
    request<Machine[]>(`/api/masters/machines${stage ? `?stage=${stage}` : ""}`),
  reasonCodes: () => request<ReasonCode[]>("/api/masters/reason-codes"),

  // -- Stock --------------------------------------------------------------
  stock: (locationCode: string, asOn?: string) =>
    request<StockResponse>(
      `/api/stock/${locationCode}${asOn ? `?as_on=${asOn}` : ""}`
    ),

  // -- Purchase -----------------------------------------------------------
  createPurchase: (payload: unknown) =>
    request("/api/purchase", { method: "POST", body: payload, queueable: true }),

  // -- Production ---------------------------------------------------------
  createRun: (payload: RunPayload) =>
    request<Run>("/api/runs", { method: "POST", body: payload, queueable: true }),

  runVariance: (runId: number) =>
    request<Variance>(`/api/runs/${runId}/variance`),

  submitRun: (runId: number, reasonCodeId?: number, note?: string) =>
    request<Run>(`/api/runs/${runId}/submit`, {
      method: "POST",
      body: { reason_code_id: reasonCodeId ?? null, variance_note: note ?? null },
    }),

  approveRun: (runId: number) =>
    request<Run>(`/api/runs/${runId}/approve`, { method: "POST" }),

  pendingRuns: () => request<Run[]>("/api/runs/pending"),

  // -- Loom floor ---------------------------------------------------------
  loomFloor: (businessDate: string, shift: string) =>
    request<LoomCell[]>(
      `/api/looms/floor?business_date=${businessDate}&shift=${shift}`
    ),

  recordLoomShift: (payload: {
    business_date: string;
    shift: string;
    lines: LoomLineIn[];
  }) =>
    request<LoomShiftResult>("/api/looms/shift", {
      method: "POST",
      body: payload,
      queueable: true,
    }),

  /** Set (or clear, with null) a loom's standing SKU. */
  assignLoomSku: (loomNo: number, skuCode: string | null) =>
    request<{ loom_no: number; sku_code: string | null }>(
      `/api/looms/${loomNo}/sku`,
      { method: "PUT", body: { sku_code: skuCode } }
    ),

  // -- Recipe -------------------------------------------------------------
  checkRecipe: (
    colourCode: string,
    businessDate: string,
    consumption: Record<string, number>
  ) =>
    request<RecipeResult>("/api/recipe/check", {
      method: "POST",
      body: {
        colour_code: colourCode,
        business_date: businessDate,
        consumption,
      },
    }),

  colours: () => request<string[]>("/api/recipe/colours"),

  // -- Printing -----------------------------------------------------------
  inkDaily: (businessDate: string) =>
    request<InkDaily>(`/api/printing/daily?business_date=${businessDate}`),

  // -- Dashboards ---------------------------------------------------------
  // Any pair of dates; the server compares with the same-shaped period
  // immediately before (see `previousForBoard`).
  recyclingBoard: (period: Period, compare: Compare = "prior") =>
    request<RecyclingBoard>(
      `/api/dashboard/recycling?${periodQuery(period, compare)}`
    ),

  inkBoard: (period: Period, compare: Compare = "prior") =>
    request<InkBoard>(
      `/api/dashboard/ink?${periodQuery(period, compare)}`
    ),

  dispatchBoard: (period: Period, compare: Compare = "prior") =>
    request<DispatchBoard>(
      `/api/dashboard/dispatch?${periodQuery(period, compare)}`
    ),

  executive: (period: Period, compare: Compare = "prior") =>
    request<ExecutiveBoard>(
      `/api/dashboard/executive?${periodQuery(period, compare)}`
    ),

  // -- AI -----------------------------------------------------------------
  aiStatus: () => request<AiStatus>("/api/ai/status"),

  ask: (question: string) =>
    request<AskResult>("/api/ai/ask", { method: "POST", body: { question } }),
};
